import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiRepeat,
  FiSearch,
  FiRefreshCw,
  FiCopy,
  FiCheck,
  FiX,
  FiAlertTriangle,
  FiChevronLeft,
  FiChevronRight,
  FiRotateCcw,
} from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { usePermission } from "../../context/PermissionContext";
import { fetchEventList } from "../../utils/eventApi";
import {
  fetchExchangeOptions,
  fetchExchangeTransactions,
  approveExchangeOrder,
  rejectExchangeOrder,
  revertExchangeOrder,
} from "../../utils/exchangeApi";

/**
 * Event Exchange orders — the review queue.
 *
 * Every order waits here as Pending, with its points already taken:
 *   - PX: approving adds it to the user's in-app balance.
 *   - Crypto: someone sends the coins, then approves with the TXID.
 * Rejecting gives the points back while the order's event is still running.
 * Revert undoes an approve or a reject and puts the order back to Pending.
 */

const STATUS_TABS = ["Pending", "Completed", "Rejected", "All"];

const STATUS_BADGE = {
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
};

const fmt = (n, max = 8) =>
  n === null || n === undefined ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: max });

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const short = (value, head = 8, tail = 6) =>
  !value ? "" : value.length <= head + tail + 3 ? value : `${value.slice(0, head)}…${value.slice(-tail)}`;

const CopyText = ({ value, label }) => {
  if (!value) return <span className="text-gray-300">—</span>;
  return (
    <button
      type="button"
      title={value}
      onClick={() => {
        navigator.clipboard?.writeText(value);
        toast.success(`${label || "Value"} copied`);
      }}
      className="inline-flex items-center gap-1 font-mono text-[11px] text-gray-700 hover:text-blue-600"
    >
      {short(value)} <FiCopy size={11} />
    </button>
  );
};

/** Approve: PX is credited in-app; crypto has been sent, so record the TXID. */
const ApproveModal = ({ order, onClose, onDone }) => {
  const [txHash, setTxHash] = useState("");
  const [label, setLabel] = useState("TXID");
  const [saving, setSaving] = useState(false);
  const crypto = !!order.is_crypto;

  const submit = async (e) => {
    e.preventDefault();
    if (crypto && !txHash.trim()) return toast.error("Enter the TXID of the transfer");
    setSaving(true);
    try {
      const body = crypto ? { tx_hash: txHash.trim(), tx_id_label: label.trim() || "TXID" } : {};
      const res = await approveExchangeOrder(order.order_id, body);
      toast.success(res.message || "Order approved");
      onDone();
    } catch (err) {
      toast.error(err.message || "Could not approve");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div>
          <h3 className="text-base font-bold text-gray-800">Approve {order.order_id}</h3>
          {crypto ? (
            <p className="text-xs text-gray-500 mt-1">
              Send <strong>{fmt(order.tokens_received)} {order.target_symbol}</strong>
              {order.network ? ` (${order.network})` : ""} to{" "}
              <span className="font-mono">{order.recipient_address}</span>, then record the transfer here.
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">
              <strong>
                {fmt(order.tokens_received)} {order.target_symbol}
              </strong>{" "}
              is added to {order.user?.username || order.user?.email || "the user"}'s in-app balance now. The{" "}
              {fmt(order.points_spent)} EP was taken when they exchanged.
            </p>
          )}
        </div>
        {crypto && (
          <>
            <label className="block">
              <span className="text-xs font-semibold text-gray-600">Transaction ID</span>
              <input
                autoFocus
                value={txHash}
                onChange={(e) => setTxHash(e.target.value)}
                maxLength={255}
                placeholder="0x… or the exchange transfer ID"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-gray-600">Label shown in the app</span>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={50}
                placeholder="TXID"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-600 rounded-xl hover:bg-gray-100">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl disabled:opacity-50"
          >
            {saving ? "Approving…" : crypto ? "Approve & mark sent" : `Approve & add ${order.target_symbol}`}
          </button>
        </div>
      </form>
    </div>
  );
};

/** Reject: refunds the points while the event runs; after it closes they have expired. */
const RejectModal = ({ order, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const eventRunning = order.event?.status === "running";

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 3) return toast.error("Give a reason — the user sees it");
    setSaving(true);
    try {
      const res = await rejectExchangeOrder(order.order_id, { reason: reason.trim() });
      toast.success(res.message || "Order rejected");
      onDone();
    } catch (err) {
      toast.error(err.message || "Could not reject");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div>
          <h3 className="text-base font-bold text-gray-800">Reject {order.order_id}</h3>
          <p className="text-xs text-gray-500 mt-1">
            {fmt(order.tokens_received)} {order.target_symbol} for {fmt(order.points_spent)} EP.
          </p>
        </div>
        {eventRunning ? (
          <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-xs text-blue-800">
            The event is still running, so <strong>{fmt(order.points_spent)} EP</strong> goes back to the user's event points.
          </div>
        ) : (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex gap-2">
            <FiAlertTriangle className="shrink-0 mt-0.5" />
            <span>
              {order.event?.name || "This event"} has closed and its points have expired, so{" "}
              <strong>no points will be returned</strong>. Use adjust points on a running event if the user should be
              compensated.
            </span>
          </div>
        )}
        <label className="block">
          <span className="text-xs font-semibold text-gray-600">Reason (sent to the user)</span>
          <textarea
            autoFocus
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder="e.g. Unverified Binance KYC or invalid withdrawal address"
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-100"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-600 rounded-xl hover:bg-gray-100">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl disabled:opacity-50"
          >
            {saving ? "Rejecting…" : "Reject order"}
          </button>
        </div>
      </form>
    </div>
  );
};

/**
 * Revert: undo an approve or a reject. The order goes back to Pending, to
 * approve or reject again. What moves depends on what was decided.
 */
const RevertModal = ({ order, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const eventRunning = order.event?.status === "running";
  const amount = `${fmt(order.tokens_received)} ${order.target_symbol}`;
  const points = `${fmt(order.points_spent)} EP`;
  // The server refuses this one; say so up front instead of after a click.
  const blocked = order.status === "Rejected" && order.points_refunded && !eventRunning;

  let effect;
  if (order.status === "Completed" && !order.is_crypto) {
    effect = (
      <>
        <strong>{amount}</strong> is taken back from the user's balance. If they no longer have it, the revert is
        refused.
      </>
    );
  } else if (order.status === "Completed") {
    effect = (
      <>
        Nothing is sent or recalled for you. The TXID{" "}
        {order.tx_hash ? <span className="font-mono">{short(order.tx_hash)}</span> : ""} is cleared (the activity log
        keeps it). Revert only if the transfer failed, was marked sent by mistake, or came back.
      </>
    );
  } else if (order.points_refunded) {
    effect = (
      <>
        The <strong>{points}</strong> returned at rejection is taken from the user again. If they have spent it since,
        the revert is refused.
      </>
    );
  } else {
    effect = <>No points were returned at rejection, so nothing is taken.</>;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 3) return toast.error("Give a reason for the revert");
    setSaving(true);
    try {
      const res = await revertExchangeOrder(order.order_id, { reason: reason.trim() });
      toast.success(res.message || "Order reverted to Pending");
      onDone();
    } catch (err) {
      toast.error(err.message || "Could not revert");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div>
          <h3 className="text-base font-bold text-gray-800">Revert {order.order_id}</h3>
          <p className="text-xs text-gray-500 mt-1">
            {order.status} · {amount} for {points}. The order goes back to <strong>Pending</strong>, to approve or
            reject again, and the user is notified.
          </p>
        </div>
        {blocked ? (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex gap-2">
            <FiAlertTriangle className="shrink-0 mt-0.5" />
            <span>
              The {points} was returned to the user and {order.event?.name || "the event"} has ended, so its scoreboard
              is final. This rejection cannot be reverted. Use adjust points on a running event instead.
            </span>
          </div>
        ) : (
          <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-xs text-blue-800">{effect}</div>
        )}
        <label className="block">
          <span className="text-xs font-semibold text-gray-600">Reason (kept in the activity log)</span>
          <textarea
            autoFocus
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            disabled={blocked}
            placeholder="e.g. Approved by mistake, or a multi-account found later"
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-100 disabled:bg-gray-50"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-600 rounded-xl hover:bg-gray-100">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || blocked}
            className="px-4 py-2 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-xl disabled:opacity-50"
          >
            {saving ? "Reverting…" : "Revert to Pending"}
          </button>
        </div>
      </form>
    </div>
  );
};

const ExchangeOrders = () => {
  const { hasPermission } = usePermission();
  const canReview = hasPermission("exchange", "edit");
  const canViewUsers = hasPermission("users", "view");

  // An event's Exchange tab links here with ?event=<id>.
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("Pending");
  const [symbol, setSymbol] = useState("");
  const [eventId, setEventId] = useState(searchParams.get("event") || "");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);

  const [events, setEvents] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reverting, setReverting] = useState(null);

  useEffect(() => {
    fetchEventList({ page: 1, limit: 100 })
      .then((res) => setEvents(res?.data || []))
      .catch(() => setEvents([]));
    fetchExchangeOptions()
      .then((res) => setSymbols((res?.data || []).map((o) => o.symbol_name)))
      .catch(() => setSymbols([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchExchangeTransactions({ status, symbol, search, eventId, page, limit: 25 });
      setRows(res.data || []);
      setCounts(res.counts || {});
      setPagination(res.pagination || { current_page: 1, total_pages: 1, total: 0 });
    } catch (err) {
      toast.error(err.message || "Could not load exchange orders");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, symbol, search, eventId, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Search on a short pause rather than per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const done = () => {
    setApproving(null);
    setRejecting(null);
    setReverting(null);
    load();
  };

  const totalAll = (counts.Pending || 0) + (counts.Completed || 0) + (counts.Rejected || 0);

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-7xl mx-auto space-y-5">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FiRepeat className="text-blue-600" /> Exchange Orders
                </h1>
                <p className="text-xs text-gray-400 mt-1">
                  Event points converted to PX or crypto. Every order waits here: approving PX adds it to the user's
                  balance, and crypto is approved with its TXID once sent. Revert undoes an approve or reject.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={eventId}
                  onChange={(e) => {
                    setEventId(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-700 max-w-[220px]"
                >
                  <option value="">All events</option>
                  {events.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
                <select
                  value={symbol}
                  onChange={(e) => {
                    setSymbol(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-700"
                >
                  <option value="">All currencies</option>
                  {symbols.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                  <input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Order ID, email, address, TXID"
                    className="pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-xs w-64 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <button
                  onClick={load}
                  className="p-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50"
                  title="Refresh"
                >
                  <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} />
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-center gap-1 px-4 py-3 border-b border-gray-100 bg-gray-50/50 overflow-x-auto">
                {STATUS_TABS.map((key) => {
                  const count = key === "All" ? totalAll : counts[key] || 0;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        setStatus(key);
                        setPage(1);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition flex items-center gap-1.5 ${
                        status === key ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-100"
                      }`}
                    >
                      {key}
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                          key === "Pending" && count > 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-3">Order</th>
                      <th className="text-left px-4 py-3">User</th>
                      <th className="text-right px-4 py-3">Receives</th>
                      <th className="text-right px-4 py-3">Points</th>
                      <th className="text-left px-4 py-3">Recipient</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3">TXID / Reason</th>
                      {canReview && <th className="text-right px-4 py-3">Action</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {!loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={canReview ? 8 : 7} className="px-4 py-12 text-center text-gray-400">
                          No {status === "All" ? "" : status.toLowerCase()} orders.
                        </td>
                      </tr>
                    )}
                    {rows.map((row) => (
                      <tr key={row.order_id} className="hover:bg-gray-50/60 align-top">
                        <td className="px-4 py-3">
                          <div className="font-mono font-semibold text-gray-800">{row.order_id}</div>
                          <div className="text-[10px] text-gray-400 mt-0.5">{formatDate(row.created_at)}</div>
                          <div className="text-[10px] text-gray-400 truncate max-w-[160px]" title={row.event?.name}>
                            {row.event?.name}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {canViewUsers ? (
                            <Link
                              to={`/user-full-details/${row.user?.user_db_id}`}
                              className="font-medium text-blue-600 hover:underline"
                            >
                              {row.user?.username || row.user?.user_id}
                            </Link>
                          ) : (
                            <span className="font-medium text-gray-800">{row.user?.username || row.user?.user_id}</span>
                          )}
                          <div className="text-[10px] text-gray-400">{row.user?.email}</div>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <span className="font-bold" style={{ color: row.color_hex || undefined }}>
                            {fmt(row.tokens_received)} {row.target_symbol}
                          </span>
                          {row.network && <div className="text-[10px] text-gray-400">{row.network}</div>}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="font-medium text-gray-700">{fmt(row.points_spent)} EP</div>
                          <div className="text-[10px] text-gray-400">
                            {fmt(row.rate)} event points = 1 {row.target_symbol}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {row.is_crypto ? <CopyText value={row.recipient_address} label="Address" /> : <span className="text-gray-400">In-app balance</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-md border text-[10px] font-semibold ${STATUS_BADGE[row.status] || ""}`}>
                            {row.status}
                          </span>
                          {row.status === "Rejected" && (
                            <div className="text-[10px] mt-1 text-gray-400">
                              {row.points_refunded ? "Points returned" : "Not refunded (event closed)"}
                            </div>
                          )}
                          {row.reviewed_by && (
                            <div className="text-[10px] mt-1 text-gray-400" title={formatDate(row.reviewed_at)}>
                              by {row.reviewed_by.email || `#${row.reviewed_by.id}`}
                            </div>
                          )}
                          {row.revert_count > 0 && (
                            <div
                              className="text-[10px] mt-1 text-amber-700 max-w-[180px]"
                              title={`${formatDate(row.reverted_at)}${row.reverted_by ? ` by ${row.reverted_by.email || `#${row.reverted_by.id}`}` : ""}`}
                            >
                              <span className="inline-flex items-center gap-1 font-semibold">
                                <FiRotateCcw size={10} /> Reverted{row.revert_count > 1 ? ` ×${row.revert_count}` : ""}
                              </span>
                              {row.revert_reason && <div className="text-gray-500 break-words">{row.revert_reason}</div>}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-[220px]">
                          {row.status === "Rejected" ? (
                            <span className="text-gray-600 break-words">{row.reject_reason}</span>
                          ) : row.tx_hash ? (
                            <div>
                              <span className="text-[10px] text-gray-400 mr-1">{row.tx_id_label}</span>
                              <CopyText value={row.tx_hash} label="TXID" />
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        {canReview && (
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {row.status === "Pending" && (
                              <div className="inline-flex gap-1.5">
                                <button
                                  onClick={() => setApproving(row)}
                                  className="px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold inline-flex items-center gap-1"
                                >
                                  <FiCheck size={12} /> Approve
                                </button>
                                <button
                                  onClick={() => setRejecting(row)}
                                  className="px-2.5 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 font-semibold inline-flex items-center gap-1"
                                >
                                  <FiX size={12} /> Reject
                                </button>
                              </div>
                            )}
                            {(row.status === "Completed" || row.status === "Rejected") && (
                              <button
                                onClick={() => setReverting(row)}
                                className="px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 font-semibold inline-flex items-center gap-1"
                              >
                                <FiRotateCcw size={12} /> Revert
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
                  <span>
                    Page {pagination.current_page} of {pagination.total_pages} · {pagination.total} orders
                  </span>
                  <div className="flex gap-1">
                    <button
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                      className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
                    >
                      <FiChevronLeft />
                    </button>
                    <button
                      disabled={page >= pagination.total_pages}
                      onClick={() => setPage((p) => p + 1)}
                      className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {approving && <ApproveModal order={approving} onClose={() => setApproving(null)} onDone={done} />}
      {rejecting && <RejectModal order={rejecting} onClose={() => setRejecting(null)} onDone={done} />}
      {reverting && <RevertModal order={reverting} onClose={() => setReverting(null)} onDone={done} />}
    </div>
  );
};

export default ExchangeOrders;
